import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import * as api from '../../services/api';

const WINDOWS = ['1m', '10m', '30m', '1h', '1d', '1w'] as const;

interface AdminHealthProps {
  embedded?: boolean;
}

const AdminHealth: React.FC<AdminHealthProps> = ({ embedded = false }) => {
  const navigate = useNavigate();
  const isAllowedAdmin = useSelector((s: RootState) => s.user.isAllowedAdmin);
  const profile = useSelector((s: RootState) => s.user.profile);
  const [data, setData] = useState<{
    backend?: { cpu_percent?: number | null; memory_percent?: number | null; memory_mb?: number | null; api_requests?: Record<string, number> };
    logins?: { success?: Record<string, number>; failed?: Record<string, number> };
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!profile) {
      navigate('/login');
      return;
    }
    if (isAllowedAdmin !== true) {
      navigate('/');
      return;
    }
    api.adminHealth()
      .then((res) => setData(res?.data ?? null))
      .catch((e) => setErr(e?.message || 'Failed to load health'))
      .finally(() => setLoading(false));
  }, [profile, isAllowedAdmin, navigate]);

  if (!profile || isAllowedAdmin !== true) return null;

  const backend = data?.backend;
  const logins = data?.logins;

  return (
    <div style={{ padding: embedded ? 0 : 24 }}>
      {!embedded && (
        <p style={{ marginBottom: 16 }}>
          <Link to="/admin/users" style={{ marginRight: 16 }}>Users</Link>
          <Link to="/admin/health" style={{ fontWeight: 600, marginRight: 16 }}>Health</Link>
          <Link to="/admin/portal">Home</Link>
        </p>
      )}
      {!embedded && <h1>Admin – Health</h1>}
      {err && <div style={{ color: 'red' }}>{err}</div>}
      {loading && <div>Loading...</div>}
      {data && !loading && (
        <>
          {backend && (
            <section style={{ marginBottom: 24, background: '#f8fafc', padding: 16, borderRadius: 8 }}>
              <h2 style={{ fontSize: '1.1rem', marginBottom: 12 }}>Backend</h2>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                {backend.cpu_percent != null && (
                  <div style={{ padding: 12, background: '#e2e8f0', borderRadius: 8, minWidth: 100 }}>
                    <div style={{ fontSize: 12, color: '#64748b' }}>CPU %</div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 600 }}>{backend.cpu_percent}%</div>
                  </div>
                )}
                {backend.memory_percent != null && (
                  <div style={{ padding: 12, background: '#e2e8f0', borderRadius: 8, minWidth: 100 }}>
                    <div style={{ fontSize: 12, color: '#64748b' }}>Memory %</div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 600 }}>{backend.memory_percent}%</div>
                  </div>
                )}
                {backend.memory_mb != null && (
                  <div style={{ padding: 12, background: '#e2e8f0', borderRadius: 8, minWidth: 100 }}>
                    <div style={{ fontSize: 12, color: '#64748b' }}>Memory (MB)</div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 600 }}>{backend.memory_mb}</div>
                  </div>
                )}
              </div>
              <h3 style={{ fontSize: 14, marginTop: 16, marginBottom: 8 }}>API requests</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {WINDOWS.map((w) => (
                  <div key={w} style={{ padding: 8, background: '#e2e8f0', borderRadius: 6 }}>
                    <span style={{ fontSize: 12, color: '#64748b' }}>{w}</span>
                    <span style={{ marginLeft: 8, fontWeight: 600 }}>{backend.api_requests?.[w] ?? 0}</span>
                  </div>
                ))}
              </div>
            </section>
          )}
          {logins && (
            <section style={{ marginBottom: 24, background: '#f8fafc', padding: 16, borderRadius: 8 }}>
              <h2 style={{ fontSize: '1.1rem', marginBottom: 12 }}>Logins</h2>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                {WINDOWS.map((w) => (
                  <div key={w} style={{ padding: 12, background: '#e2e8f0', borderRadius: 8, minWidth: 100 }}>
                    <div style={{ fontSize: 12, color: '#64748b' }}>{w}</div>
                    <div style={{ fontSize: 14 }}>✓ {logins.success?.[w] ?? 0}</div>
                    <div style={{ fontSize: 14, color: '#b91c1c' }}>✗ {logins.failed?.[w] ?? 0}</div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
};

export default AdminHealth;
